<template>
  <div class="mx-auto max-w-3xl space-y-10 pb-4">
    <header class="space-y-3">
      <h1 class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
        {{ t('landing.troubleshooting.title') }}
      </h1>
      <p class="text-lg leading-relaxed text-gray-600">
        {{ t('landing.troubleshooting.lead') }}
      </p>
    </header>

    <section class="space-y-4">
      <article
        v-for="(item, index) in faqItems"
        :key="item.key"
        class="relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-6 shadow-sm ring-1 ring-gray-100 sm:p-7"
      >
        <div class="flex items-start gap-4">
          <span
            class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-bold text-primary-700 ring-1 ring-primary-100"
          >
            {{ index + 1 }}
          </span>
          <div class="min-w-0 flex-1">
            <h2 class="text-lg font-semibold text-gray-900">
              {{ item.q }}
            </h2>
            <p class="mt-3 whitespace-pre-line leading-relaxed text-gray-700">
              {{ item.a }}
            </p>
            <figure
              v-if="item.image"
              class="mt-5"
            >
              <img
                :src="item.image.src"
                :alt="item.image.alt"
                loading="lazy"
                class="mx-auto max-h-80 w-full max-w-sm rounded-xl border border-gray-200 object-contain shadow-sm"
              >
              <figcaption
                v-if="item.image.caption"
                class="mt-2 text-center text-sm text-gray-500"
              >
                {{ item.image.caption }}
              </figcaption>
            </figure>
          </div>
        </div>
      </article>
    </section>

    <section
      class="rounded-2xl border border-primary-100 bg-gradient-to-br from-primary-50/70 via-white to-sky-50/40 p-6 shadow-sm sm:p-8"
    >
      <h2 class="text-xl font-bold text-gray-900">
        {{ t('landing.troubleshooting.contact_heading') }}
      </h2>
      <p class="mt-2 leading-relaxed text-gray-700">
        {{ t('landing.troubleshooting.contact_lead') }}
      </p>
      <div class="mt-5">
        <UButton
          :to="localePath('/contact')"
          color="primary"
          icon="i-heroicons-envelope"
        >
          {{ t('landing.troubleshooting.contact_cta') }}
        </UButton>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
const { t } = useI18n()
const localePath = useLocalePath()

const itemKeys = [
  'unlock',
  'lock',
  'rent_unlock',
  'spinner',
  'unlock_button',
  'not_locked',
  'not_at_port',
  'no_internet',
  'app_store',
] as const

type ItemKey = typeof itemKeys[number]

// Optional illustration per FAQ item. The alt/caption strings are read from
// i18n at `landing.troubleshooting.items.<key>.image_{alt,caption}`.
const itemImageSources: Partial<Record<ItemKey, string>> = {
  unlock: '/images/help/ring_spoke.jpg',
}

const faqItems = computed(() =>
  itemKeys.map((key) => {
    const src = itemImageSources[key]
    return {
      key,
      q: t(`landing.troubleshooting.items.${key}.q`),
      a: t(`landing.troubleshooting.items.${key}.a`),
      image: src
        ? {
            src,
            alt: t(`landing.troubleshooting.items.${key}.image_alt`),
            caption: t(`landing.troubleshooting.items.${key}.image_caption`),
          }
        : undefined,
    }
  })
)

useHead(() => ({
  title: t('landing.troubleshooting.meta_title'),
  meta: [{ name: 'description', content: t('landing.troubleshooting.meta_description') }],
}))
</script>
